import { useCallback, useEffect } from 'react';

import { AbsoluteRoutes, LocalStorageKeys, NotificationType } from '../enums';
import { LocalStorageService } from '../services';
import type { TFullExpertData } from '../types';
import { URLS } from '../constants';
import { activeRequestsAtom } from '../../atoms';
import axios from 'axios';
import api from '../services/api';
import { fullDataAtom } from '../../atoms/fullDataAtom';
import { useAddNotification } from './useNotificationHandlers';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from './useTranslation';

function extractExternalId(inviteLink: string): string | null {
  const match = inviteLink.match(/([a-f0-9]{32,})$/i);
  return match ? match[1] : null;
}

export function useGetFullData(expertId?: string) {
  const setFullExpertData = useSetRecoilState(fullDataAtom);
  const setActiveRequests = useSetRecoilState(activeRequestsAtom);
  const navigate = useNavigate();
  const pushNotification = useAddNotification();
  const { t } = useTranslation();

  const fetchByExternalId = useCallback(
    (externalId: string) => {
      return axios
        .get(`${URLS.API_URL}${URLS.INVITE_INFO}${externalId}`)
        .then(res => {
          const { data } = res as { data: TFullExpertData };
          setFullExpertData(data);
        });
    },
    [setFullExpertData],
  );

  const fetchFullData = useCallback(() => {
    const accessToken = LocalStorageService.getItem(LocalStorageKeys.AccessToken);
    const uuid = LocalStorageService.getItem(LocalStorageKeys.Uuid);

    if (expertId && !accessToken && !uuid) {
      // ProtectedRoute allows entry with either an AccessToken or an invite Uuid — this endpoint
      // needs one of the two as well, otherwise the page silently renders empty instead of
      // explaining why nothing loaded.
      navigate(AbsoluteRoutes.login);
      return;
    }

    setActiveRequests(prev => prev + 1);

    // An unauthenticated visitor arriving via an invite link only has a Uuid (no AccessToken) —
    // EXPERT_FULL_DATA requires a real session, so fall back to the same anonymous INVITE_INFO
    // lookup the invite flow itself uses, rather than requiring login just to view the page.
    const request =
      expertId && accessToken
        ? api.get(`${URLS.API_URL}${URLS.EXPERT_FULL_DATA}${expertId}`)
        : axios.get(`${URLS.API_URL}${URLS.INVITE_INFO}${uuid}`);

    request
      .then(async res => {
        const { data } = res as { data: TFullExpertData | string };

        if (typeof data === 'string') {
          const externalId = extractExternalId(data);

          if (!externalId) {
            console.log('Could not extract external id from invite link:', data);
            setFullExpertData(null);
            return;
          }

          await fetchByExternalId(externalId);
          return;
        }

        setFullExpertData(data);
      })
      .catch(error => {
        console.log(error);
        setFullExpertData(null);
        pushNotification({
          type: NotificationType.Error,
          title: t('Web.Title_load_error'),
          text: t('Web.Error_expert_load_failed'),
        });
      })
      .finally(() => setActiveRequests(prev => prev - 1));
  }, [expertId, setActiveRequests, setFullExpertData, fetchByExternalId, navigate, pushNotification, t]);

  useEffect(() => {
    fetchFullData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertId]);
}